import { Download, Restore } from '@mui/icons-material';
import { Dialog, DialogTitle, Typography, DialogContent, Grid, DialogActions, Button } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { DatePickerWithDataLookup, GenericAutocomplete } from '../../../Components/CommonInputs';
import { SelectOptionDto } from '../../../dtos';
import { useCreateOrderDownloadSelectedOrdersMutation, useGetCustomerForOrderDateQuery, useGetJobForOrderDateQuery, useGetOrderForOrderDateQuery, useGetSiteForOrderDateQuery, useLazyGetDispatchOrderManagementCurrentMonthQuery } from '../../../store/generated/generatedApi';
import { format, isValid } from 'date-fns';
import { exportToPdf } from '../../../util';
import { useFailedActionSnackbar, useSuccessfulActionSnackbar } from '../../../Components/CoreLib/library';

export interface IOrderDownloadModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    defaultDate: Date;
}

export const OrderDownloadModal: FC<IOrderDownloadModalProps> = props => {
    const { open, onClose, defaultDate } = props;

    const [site, setSite] = useState<SelectOptionDto>();

    const [order, setOrder] = useState<SelectOptionDto>();
    const [job, setJob] = useState<SelectOptionDto>();
    const [customer, setCustomer] = useState<SelectOptionDto>();
    const [date, setDate] = useState<Date>(defaultDate);

    const [getCurrentMonthDispatch, { data, isFetching }] = useLazyGetDispatchOrderManagementCurrentMonthQuery();
    const [daysWithData, setDaysWithData] = useState<string[]>([]);
    const [month, setMonth] = useState(date);

    const [downloadSelectedPdfs, { isSuccess, isError, error, isLoading: isDownloadingPdfs, reset }] = useCreateOrderDownloadSelectedOrdersMutation();

    useSuccessfulActionSnackbar('downloaded', 'order(s)', isSuccess, reset);
    useFailedActionSnackbar('download', 'order(s)', isError, reset, error);

    const handleDownload = useCallback(() => {
        if (!isValid(date)) {
            return;
        }
        downloadSelectedPdfs({
            orderIds: [],
            siteId: site?.id,
            orderId: order?.id,
            jobId: job?.id,
            customerId: customer?.id,
            orderDate: date
        }).then((response: any) => {
            if (response.data) {
                let title = 'Orders';
                if (site) {
                    title += '-' + site.label
                }
                if (customer) {
                    title += '-' + customer.label
                }
                if (job) {
                    title += '-' + job.label
                }
                if (order) {
                    title += '-' + order.label
                }
                if (date) {
                    title += '-' + format(date, 'yyyy-MM-dd');
                }
                exportToPdf(response.data, `${title}.pdf`.replace(/\s+/g, ''));
            }
        })
    }, [customer, date, downloadSelectedPdfs, job, order, site]);

    const loadData = useCallback(
        (date: Date) => {
            const formattedDate = format(date, 'yyyy-MM');
            getCurrentMonthDispatch({ startDate: formattedDate });
        },
        [getCurrentMonthDispatch]
    );

    useEffect(() => {
        setDate(defaultDate);
    }, [defaultDate]);

    useEffect(() => {
        loadData(month);
    }, [month, loadData]);

    useEffect(() => {
        if (!isFetching) {
            setDaysWithData(data ?? []);
        }
    }, [data, isFetching]);

    const onMonthChange = useCallback((newMonth: Date) => {
        setDaysWithData([]);
        setMonth(newMonth);
    }, []);

    const handleConfirm = useCallback(() => {
        handleDownload();
    }, [handleDownload]);

    const handleOnClose = useCallback(() => {
        setSite(undefined);
        setOrder(undefined);
        setJob(undefined);
        setCustomer(undefined);
        setDate(defaultDate);
        onClose();
    }, [onClose, defaultDate]);

    return (
        <Dialog onClose={handleOnClose} open={open} fullWidth>
            <DialogTitle sx={{ backgroundColor: 'primary', color: '#ffffff' }}>
                <Typography variant='h2'>
                    {<Restore />} Download Order Details
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container direction='column' mt={2}>
                    <Grid item>
                        <Typography>Select a date and or site to download a filtered list of order line items.</Typography>
                    </Grid>
                    <Grid item container spacing={2} alignItems={'center'}>
                        <Grid item xs={12}>
                            <DatePickerWithDataLookup
                                date={date}
                                updateDate={setDate}
                                daysWithData={daysWithData}
                                handleOnMonthChange={onMonthChange}
                                label={'Order Date'}
                                required
                            />
                        </Grid>
                        <Grid item container spacing={2}>
                            <Grid item xs={6}>
                                <GenericAutocomplete
                                    label={'Site'}
                                    selectedId={site?.id ?? ''}
                                    handleSelectedChange={setSite}
                                    query={useGetSiteForOrderDateQuery}
                                    queryParameters={{ orderDate: date.toISOString() }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <GenericAutocomplete
                                    label={'Customer'}
                                    selectedId={customer?.id ?? ''}
                                    handleSelectedChange={setCustomer}
                                    query={useGetCustomerForOrderDateQuery}
                                    queryParameters={{ orderDate: date.toISOString() }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item container spacing={2}>
                            <Grid item xs={6}>
                                <GenericAutocomplete
                                    label={'Job'}
                                    selectedId={job?.id ?? ''}
                                    handleSelectedChange={setJob}
                                    query={useGetJobForOrderDateQuery}
                                    queryParameters={{ orderDate: date.toISOString() }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <GenericAutocomplete
                                    label={'Order'}
                                    selectedId={order?.id ?? ''}
                                    handleSelectedChange={setOrder}
                                    query={useGetOrderForOrderDateQuery}
                                    queryParameters={{ orderDate: date.toISOString() }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' onClick={handleOnClose} disabled={isDownloadingPdfs}>
                    Close
                </Button>
                <Button startIcon={<Download />} variant='contained' onClick={handleConfirm} disabled={isDownloadingPdfs}>
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
}